import {useMemo} from 'react'

import {TENANT_NO_STORAGE_RESIDENCY_REGION} from 'definitions/tenantConfig'
import {useAnalyticsTenantDomainGate} from 'features/Analytics/hooks'
import {useActions} from 'hooks'
import {useTenantConfigQuery} from 'hooks/useTenantConfigQuery'

import {FeatureHighlightMeta} from './types'

export type FeatureHighlightEligibility = {
  /** True when the highlight may be shown (admin + tenant gates pass). */
  enabled: boolean
  /**
   * True while analytics tenant eligibility is still loading.
   * Distinct from permanently ineligible (e.g. non-admin).
   */
  isEligibilityPending: boolean
}

/**
 * Resolves highlight eligibility from feature meta flags.
 *
 * Analytics residency matches `withAnalyticsAIUsageRouteGuard`: only block when
 * no-storage is confirmed. Fail closed while tenant config is still loading to
 * avoid a flash; fail open on tenant-config error so discovery still works.
 */
export const useFeatureHighlightEnabled = (
  feature: FeatureHighlightMeta,
): FeatureHighlightEligibility => {
  const {doIHaveAccess} = useActions()
  const {isAnalyticsDisabledForPrimaryDomain} = useAnalyticsTenantDomainGate()
  const tenantConfigQuery = useTenantConfigQuery()

  const requiresAdmin = feature.requiresAdmin === true
  const isAdminIneligible = requiresAdmin && !doIHaveAccess

  const analyticsEligibility = useMemo(() => {
    if (!feature.requiresAnalyticsTenantAccess) {
      return {hasAccess: true, isPending: false}
    }
    if (isAnalyticsDisabledForPrimaryDomain) {
      return {hasAccess: false, isPending: false}
    }

    const isTenantConfigPending =
      !tenantConfigQuery.isSuccess &&
      (tenantConfigQuery.isLoading || tenantConfigQuery.isFetching)

    // Avoid flashing the snackbar before residency is known.
    if (isTenantConfigPending) {
      return {hasAccess: false, isPending: true}
    }

    const isNoStorageTenantResidency =
      tenantConfigQuery.isSuccess &&
      tenantConfigQuery.data?.data?.residencyRegion ===
        TENANT_NO_STORAGE_RESIDENCY_REGION

    return {
      hasAccess: !isNoStorageTenantResidency,
      isPending: false,
    }
  }, [
    feature.requiresAnalyticsTenantAccess,
    isAnalyticsDisabledForPrimaryDomain,
    tenantConfigQuery.data,
    tenantConfigQuery.isFetching,
    tenantConfigQuery.isLoading,
    tenantConfigQuery.isSuccess,
  ])

  if (isAdminIneligible) {
    return {enabled: false, isEligibilityPending: false}
  }

  return {
    enabled: analyticsEligibility.hasAccess,
    isEligibilityPending: analyticsEligibility.isPending,
  }
}
